/*
 * Generates the overall layout for the Ticket page. Lifted from the old STV app
 */

import React from "react";
import clsx from "clsx";
import { makeStyles, Theme } from "@material-ui/core/styles";
import { CssBaseline, Drawer, AppBar, Toolbar, Typography, IconButton, Container } from "@material-ui/core";
import MenuIcon from "@material-ui/icons/Menu";
import ChevronRightIcon from "@material-ui/icons/ChevronRight";
import LightModeIcon from "@material-ui/icons/WbIncandescent";
import DarkModeIcon from "@material-ui/icons/WbIncandescentOutlined";
import PageTabs from "./PageTabs";
import { usePersistedState } from "../hooks";
import { TIME_ZONE_DISPLAY } from "../helpers/dataHelper";

const drawerWidth = 360;

const useStyles = makeStyles((theme: Theme) => ({
    root: {
        display: "flex"
    },
    toolbar: {
        paddingRight: 24 // keep right padding when drawer closed
    },
    toolbarIcon: {
        display: "flex",
        alignItems: "center",
        justifyContent: "flex-end",
        padding: "0 8px",
        ...theme.mixins.toolbar
    },
    appBar: {
        zIndex: theme.zIndex.drawer + 1,
        transition: theme.transitions.create(["width", "margin"], {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.leavingScreen
        }),
        backgroundColor: "#4fbddb",
        color: "#2f2d2d"
    },
    appBarShift: {
        width: `calc(100vw - ${drawerWidth}px)`,
        transition: theme.transitions.create(["margin", "width"], {
            easing: theme.transitions.easing.easeOut,
            duration: theme.transitions.duration.enteringScreen
        }),
        marginRight: drawerWidth
    },
    menuButtonHidden: {
        display: "none"
    },
    title: {
        flexGrow: 1
    },
    drawerPaper: {
        position: "relative",
        whiteSpace: "nowrap",
        width: drawerWidth,
        transition: theme.transitions.create("width", {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.enteringScreen
        }),
        height: "100vh",
        overflow: "auto"
    },
    drawerPaperClose: {
        overflowX: "hidden",
        transition: theme.transitions.create("width", {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.leavingScreen
        }),
        width: 0
    },
    drawerHeader: {
        display: "flex",
        alignItems: "center",
        padding: "0 8px",
        ...theme.mixins.toolbar,
        justifyContent: "flex-start"
    },
    drawerContents: {
        padding: "0 1rem"
    },
    appBarSpacer: theme.mixins.toolbar,
    content: {
        flexGrow: 1,
        overflow: "auto",
        height: "100vh"
    },
    container: {
        paddingTop: theme.spacing(4),
        paddingBottom: theme.spacing(4),
        height: "calc(100vh - 64px)",
        overflow: "auto"
    },
    paper: {
        padding: theme.spacing(2),
        display: "flex",
        overflow: "auto",
        flexDirection: "column"
    },
    fixedHeight: {
        height: 240
    },
    timeZone: {
        padding: "12px",
        fontWeight: 500,
        color: "rgba(0, 0, 0, 0.54)",
        fontSize: "12px"
    }
}));

interface LayoutProps {
    drawer?: React.ReactNode;
    setDarkMode?: (on: boolean) => void;
    darkMode?: boolean;
}

const Layout: React.FC<LayoutProps> = ({ setDarkMode = null, darkMode = null, drawer = null, children }) => {
    const classes = useStyles();
    const [open, setOpen] = usePersistedState(true, "sidebar-open");
    const handleDrawerOpen = () => {
        setOpen(true);
    };
    const handleDrawerClose = () => {
        setOpen(false);
    };

    const hasDrawer = drawer !== null;

    return (
        <div className={classes.root}>
            <CssBaseline />
            <AppBar
                position="absolute"
                className={clsx(
                    //fast way of doing class name
                    classes.appBar,
                    hasDrawer && open && classes.appBarShift
                )}
            >
                <Toolbar className={classes.toolbar}>
                    <PageTabs />

                    <Typography component="h1" variant="h6" color="inherit" noWrap className={classes.title}></Typography>

                    <Typography align="right" variant="overline" className={classes.timeZone}>
                        {TIME_ZONE_DISPLAY}
                    </Typography>

                    {setDarkMode !== null && (
                        <IconButton color="inherit" aria-label="open drawer" onClick={() => setDarkMode(!darkMode)} >
                            {darkMode ? <LightModeIcon /> : <DarkModeIcon />}
                        </IconButton>
                    )}
                    {hasDrawer && (
                        <IconButton
                            color="inherit"
                            aria-label="open drawer"
                            onClick={handleDrawerOpen}
                            className={clsx(open && classes.menuButtonHidden)}
                        >
                            <MenuIcon />
                        </IconButton>
                    )}
                </Toolbar>
            </AppBar>
            <main className={classes.content}>
                <div className={classes.appBarSpacer} />
                <Container maxWidth={false} className={classes.container}>
                    <div>{children}</div>
                </Container>
            </main>
            {hasDrawer && (
                <Drawer
                    variant="permanent"
                    classes={{
                        paper: clsx(classes.drawerPaper, !open && classes.drawerPaperClose)
                    }}
                    open={open}
                    anchor="right"
                >
                    <div className={classes.drawerHeader}>
                        <IconButton onClick={handleDrawerClose}>
                            <ChevronRightIcon />
                        </IconButton>
                    </div>
                    <div className={classes.drawerContents}>{drawer}</div>
                </Drawer>
            )}
        </div>
    );
};

export default Layout;
